<script>
import AutomationActionTeamMessageInput from 'dashboard/components/widgets/AutomationActionTeamMessageInput.vue';
import AutomationActionFileInput from 'dashboard/components/widgets/AutomationFileInput.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    AutomationActionTeamMessageInput,
    AutomationActionFileInput,
    WootMessageEditor,
    NextButton,
    SingleSelect,
    MultiSelect,
    NextInput,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => null,
    },
    actionTypes: {
      type: Array,
      default: () => [],
    },
    dropdownValues: {
      type: Array,
      default: () => [],
    },
    errorMessage: {
      type: String,
      default: '',
    },
    showActionInput: {
      type: Boolean,
      default: true,
    },
    initialFileName: {
      type: String,
      default: '',
    },
    isMacro: {
      type: Boolean,
      default: false,
    },
    dropdownMaxHeight: {
      type: String,
      default: 'max-h-80',
    },
  },
  emits: ['update:modelValue', 'input', 'removeAction', 'resetAction'],
  computed: {
    currentActionType() {
      return (
        this.actionTypes.find(action => action.key === this.action_name) || null
      );
    },
    action_name: {
      get() {
        return this.modelValue?.action_name || null;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_name: value });
        this.$emit('input', { ...payload, action_name: value });
      },
    },
    action_params: {
      get() {
        return this.modelValue?.action_params || null;
      },
      set(value) {
        const payload = this.modelValue || {};
        this.$emit('update:modelValue', { ...payload, action_params: value });
        this.$emit('input', { ...payload, action_params: value });
      },
    },
    inputType() {
      return this.currentActionType?.inputType || null;
    },
    customActionComponent() {
      return this.currentActionType?.component || null;
    },
    actionNameAsSelectModel: {
      get() {
        if (!this.action_name) return {};
        const found = this.actionTypes.find(a => a.key === this.action_name);
        return found ? { id: found.key, name: found.label } : {};
      },
      set(value) {
        this.action_name = value?.id || null;
      },
    },
    actionParamsAsSingleSelectModel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0] || {};
        }

        if (this.action_params && typeof this.action_params === 'object') {
          return this.action_params;
        }

        return {};
      },
      set(value) {
        this.action_params = value && Object.keys(value).length ? value : [];
      },
    },
    actionTypesAsOptions() {
      return this.actionTypes.map(action => ({
        id: action.key,
        name: action.label,
      }));
    },
    isVerticalLayout() {
      return ['team_message', 'textarea'].includes(this.inputType);
    },
    castMessageVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0];
        }
        return this.action_params;
      },
      set(value) {
        this.action_params = value;
      },
    },
    castSingleStringVmodel: {
      get() {
        if (Array.isArray(this.action_params)) {
          return this.action_params[0] ?? '';
        }
        return this.action_params ?? '';
      },
      set(value) {
        this.action_params = [value];
      },
    },
    delayUnitOptions() {
      return [
        { id: 'seconds', name: this.$t('AUTOMATION.ACTIONS.DELAY_SECONDS') },
        { id: 'minutes', name: this.$t('AUTOMATION.ACTIONS.DELAY_MINUTES') },
        { id: 'hours', name: this.$t('AUTOMATION.ACTIONS.DELAY_HOURS') },
        { id: 'days', name: this.$t('AUTOMATION.ACTIONS.DELAY_DAYS') },
      ];
    },
    delayValue: {
      get() {
        return this.getStructuredParams().value ?? '';
      },
      set(value) {
        this.setStructuredParams({ value });
      },
    },
    delayUnit: {
      get() {
        return this.getStructuredParams().unit || 'seconds';
      },
      set(value) {
        this.setStructuredParams({ unit: value });
      },
    },
    delayUnitModel: {
      get() {
        return (
          this.delayUnitOptions.find(option => option.id === this.delayUnit) ||
          null
        );
      },
      set(value) {
        this.delayUnit = value?.id || 'seconds';
      },
    },
    delayMinValue: {
      get() {
        return this.getStructuredParams().min_value ?? '';
      },
      set(value) {
        this.setStructuredParams({ min_value: value });
      },
    },
    delayMinUnit: {
      get() {
        return this.getStructuredParams().min_unit || 'seconds';
      },
      set(value) {
        this.setStructuredParams({ min_unit: value });
      },
    },
    delayMinUnitModel: {
      get() {
        return (
          this.delayUnitOptions.find(
            option => option.id === this.delayMinUnit
          ) || null
        );
      },
      set(value) {
        this.delayMinUnit = value?.id || 'seconds';
      },
    },
    delayMaxValue: {
      get() {
        return this.getStructuredParams().max_value ?? '';
      },
      set(value) {
        this.setStructuredParams({ max_value: value });
      },
    },
    delayMaxUnit: {
      get() {
        return this.getStructuredParams().max_unit || 'seconds';
      },
      set(value) {
        this.setStructuredParams({ max_unit: value });
      },
    },
    delayMaxUnitModel: {
      get() {
        return (
          this.delayUnitOptions.find(
            option => option.id === this.delayMaxUnit
          ) || null
        );
      },
      set(value) {
        this.delayMaxUnit = value?.id || 'seconds';
      },
    },
  },
  methods: {
    removeAction() {
      this.$emit('removeAction');
    },
    resetAction() {
      this.$emit('resetAction');
    },
    onActionNameChange(value) {
      this.actionNameAsSelectModel = value;
      this.resetAction();
    },
    getStructuredParams() {
      if (Array.isArray(this.action_params)) {
        return this.action_params[0] || {};
      }

      if (this.action_params && typeof this.action_params === 'object') {
        return this.action_params;
      }

      return {};
    },
    setStructuredParams(values) {
      this.action_params = {
        ...this.getStructuredParams(),
        ...values,
      };
    },
  },
};
</script>

<template>
  <li class="list-none py-2 first:pt-0 last:pb-0">
    <div
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': errorMessage }"
    >
      <div class="flex items-center gap-2">
        <SingleSelect
          :model-value="actionNameAsSelectModel"
          :options="actionTypesAsOptions"
          :dropdown-max-height="dropdownMaxHeight"
          disable-deselect
          class="flex-shrink-0"
          @update:model-value="onActionNameChange"
        />
        <template v-if="showActionInput && !isVerticalLayout">
          <SingleSelect
            v-if="inputType === 'search_select'"
            v-model="actionParamsAsSingleSelectModel"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <MultiSelect
            v-else-if="inputType === 'multi_select'"
            v-model="action_params"
            :options="dropdownValues"
            :dropdown-max-height="dropdownMaxHeight"
          />
          <NextInput
            v-else-if="inputType === 'email'"
            v-model="castSingleStringVmodel"
            type="email"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.EMAIL_INPUT_PLACEHOLDER')"
          />
          <NextInput
            v-else-if="inputType === 'url'"
            v-model="castSingleStringVmodel"
            type="url"
            size="sm"
            :placeholder="$t('AUTOMATION.ACTION.URL_INPUT_PLACEHOLDER')"
          />
          <AutomationActionFileInput
            v-else-if="inputType === 'attachment'"
            v-model="action_params"
            :initial-file-name="initialFileName"
          />
          <div
            v-else-if="inputType === 'delay_duration'"
            class="flex items-center gap-2 flex-1"
          >
            <NextInput
              v-model="delayValue"
              type="number"
              min="1"
              size="sm"
              :placeholder="$t('AUTOMATION.ACTIONS.DELAY_VALUE_PLACEHOLDER')"
            />
            <SingleSelect
              v-model="delayUnitModel"
              :options="delayUnitOptions"
              disable-search
              disable-deselect
              class="flex-1 automation-action-input__select"
            />
          </div>
          <div
            v-else-if="inputType === 'delay_duration_range'"
            class="grid grid-cols-2 gap-2 flex-1"
          >
            <div class="flex items-center gap-2">
              <NextInput
                v-model="delayMinValue"
                type="number"
                min="1"
                size="sm"
                :placeholder="$t('AUTOMATION.ACTIONS.DELAY_MIN_PLACEHOLDER')"
              />
              <SingleSelect
                v-model="delayMinUnitModel"
                :options="delayUnitOptions"
                disable-search
                disable-deselect
                class="flex-1 automation-action-input__select"
              />
            </div>
            <div class="flex items-center gap-2">
              <NextInput
                v-model="delayMaxValue"
                type="number"
                min="1"
                size="sm"
                :placeholder="$t('AUTOMATION.ACTIONS.DELAY_MAX_PLACEHOLDER')"
              />
              <SingleSelect
                v-model="delayMaxUnitModel"
                :options="delayUnitOptions"
                disable-search
                disable-deselect
                class="flex-1 automation-action-input__select"
              />
            </div>
          </div>
          <component
            :is="customActionComponent"
            v-else-if="
              inputType === 'custom_component' && customActionComponent
            "
            v-model="action_params"
            class="flex-1"
          />
        </template>
        <NextButton
          v-if="!isMacro"
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click="removeAction"
        />
      </div>
      <AutomationActionTeamMessageInput
        v-if="inputType === 'team_message'"
        v-model="action_params"
        :teams="dropdownValues"
        :dropdown-max-height="dropdownMaxHeight"
      />
      <WootMessageEditor
        v-if="inputType === 'textarea'"
        v-model="castMessageVmodel"
        rows="4"
        enable-variables
        :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
        class="[&_.ProseMirror-menubar]:hidden px-3 py-1 bg-n-alpha-1 rounded-lg outline outline-1 outline-n-weak dark:outline-n-strong"
      />
    </div>
    <span v-if="errorMessage" class="text-sm text-n-ruby-11">
      {{ errorMessage }}
    </span>
  </li>
</template>

<style lang="scss" scoped>
.automation-action-input__select:deep(button) {
  @apply w-full justify-between;
}
</style>
